import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Tree from "rc-tree";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  CircularProgress,
} from "@mui/material";
import { newTemplateActions } from "../../../../redux/slices/newTemplateSlice";
import { api } from "../../../../redux/api";
import { workflowGroupsToRcTree } from "../../../../utils/rctreeDataConversion";
import ErrorModal from "../../../ItemProperties/Forms/FieldDataValidation/ErrorModal";
import { scrollToSelectedItem } from "../../../../utils/utilFunctions";

function NtwCopyTemplate() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // State variables
  const [accountOptions, setAccountOptions] = useState([]);
  const [accountValue, setAccountValue] = useState("");
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [copyTempTreeData, setCopyTempTreeData] = useState([]);
  const [errorModal, setErrorModal] = useState(false);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const errorRef = useRef(null)
  const accountErrorRef = useRef(null)
  const parentRef = useRef(null)
  const currentScreen = useSelector(state => state.treeData.currentScreen)
  const { account } = useSelector(state => state.queryParams)
  const leftTreeSelectedNode = useSelector(state => state.treeData[state.treeData.currentScreen].leftTree.tree.body.selectedNode)
  const latestAccountRef = useRef("")
  
  const iconByType = {
    inboundTemplate: "GET_DATA",
    outboundTemplate: "SEND_DATA",
    workflowGroup: "RECORD",
  }

  const handleSelectedNode = (response, name, workflowGroup, account) => {
    if (response && response.data && response.data.data.workflow_groups && workflowGroup) {
      let selectedKey;
      let workflowKey;
      let node;
      const workflowGroups = workflowGroupsToRcTree(
        response.data.data,
        currentScreen
      );
      for (let i = 0; i < workflowGroups.length; i++) {
        const workflow = workflowGroups[i];
  
        // If workflow group matches 
        if (workflow.title === workflowGroup) {
          
          // Case when there are no children
          if (workflow.children.length === 0) {
            selectedKey = workflow.key;
            workflowKey = workflow.key;
          } 
          // Case when there are children
          else {
            const templates = workflow.children;
            for (let j = 0; j < templates.length; j++) {
              const template = templates[j];
              if (template.title === name) {
                node = template;
                node.sourceAccount = account;
                selectedKey = template.key;
                workflowKey = workflow.key;
                dispatch(newTemplateActions.setCopyTemplateTreeSelectedNode(node));
                break;
              } else {
                selectedKey = workflow.key;
                workflowKey = workflow.key;
              }
            }
          }
        }
  
        if (selectedKey) break;
      }
  
      if (selectedKey && workflowKey) {
        setSelectedKeys([selectedKey]);
        setExpandedKeys([workflowKey]);
      }
    }
  };
  
  const filterTemplates = (data) => {
    return data?.map(node => {
      if (node.children && node.children.length > 0) {
        return {
          ...node,
          children: node.children.filter(child => child.type !== 'DOCUMENT')
        }
      }
      return node
    })
  }

  // Get the tree data for the selected account
  const getTreeData = useCallback((account) => {
    setLoading(true)
    const endpoint = currentScreen === 'inbound' ? 'getCopyInboundTemplateList' : 'getCopyOutboundTemplateList'
    dispatch(
      api.endpoints[endpoint].initiate({ account })
    ).then((response) => {
      if (account !== latestAccountRef.current) return
      if (response?.data?.current_state === "SUCCESS" && response?.data?.message !== 'Failed') {
        
        // Convert the response data to tree data format
        const treeData = filterTemplates(workflowGroupsToRcTree(response?.data?.data, currentScreen)) || []
        setCopyTempTreeData(treeData);

        const template = leftTreeSelectedNode.type !== 'workflowGroup' ? leftTreeSelectedNode.title : '';
        const workflowGroup = leftTreeSelectedNode.type === 'workflowGroup' ? leftTreeSelectedNode.title : leftTreeSelectedNode.workflowGroup
        handleSelectedNode(
          response,
          template,
          workflowGroup,
          account
        );
      }
    })
    .catch(error => errorRef.current.textContent = error || error.message || t("BOX_MSG_GENERAL_ERROR"))
    .finally(()=> setLoading(false))
  }, [currentScreen, dispatch]); 

  // Get the list of accounts and set the default account and its tree data
  useEffect(() => {
    accountErrorRef.current.textContent = ""
    setAccountsLoading(true)
    dispatch(api.endpoints.getAccounts.initiate({ forceRefetch: true })).then(
      (response) => {
        if (response.error) {
          accountErrorRef.current.textContent =
            response.error.message || response.message || t("ACC_NOT_FOUND");
        }
        if (response && response.data.current_state === "SUCCESS") {
          let accounts = response?.data?.data?.accounts;
          setAccountOptions(accounts);
          if (accounts?.length !== 0) {
            const displayAccount = accounts.includes(account)
              ? account
              : accounts[0];
            setAccountValue(displayAccount);
          } else setAccountValue("");
        }
      }
    ).finally(() => setAccountsLoading(false));
  }, [account, dispatch]);

  useEffect(() => {
    if (accountValue) {
      latestAccountRef.current = accountValue
      getTreeData(accountValue);
    }
  }, [accountValue, getTreeData]);

  useEffect(() => {
    scrollToSelectedItem(selectedKeys, parentRef);
  }, [selectedKeys, copyTempTreeData]);

  const handleErrorModal = () => {
    setErrorModal(false);
  };
  // Generate dropdown items for the 'account' dropdown
  const accountDropDownItems = accountOptions?.map((obj, key) => (
    <MenuItem key={key} value={obj}>
      {obj}
    </MenuItem>
  ));

  // Handle the 'expand' event of the tree
  const onExpand = (expandedKeys) => {
    setExpandedKeys(expandedKeys);
  };

  // Handle the 'select' event of the tree
  const onSelect = (selectedKeys, info) => {
    if (info.node.isLeaf) {
      setSelectedKeys([info.node.key]);

      let srcAccount = accountValue;
      info.selectedNodes[0].sourceAccount = srcAccount;
      dispatch(
        newTemplateActions.setCopyTemplateTreeSelectedNode(
          info.selectedNodes[0]
        )
      );
    } else {
      setMsg(info.node.title);
      setErrorModal(true);
      setSelectedKeys([]);
      dispatch(newTemplateActions.setCopyTemplateTreeSelectedNode(""));
    }
  };

  // Define an error message as a JSX element wrapped in a <span> tag if selected node in the tree is not of type template
  const errorMessage = (
    <span>
      {t("BOX_SEL_FILE_TO_COPY_")}{" "}
      {/* Wrap the 'msg' variable in a <b> tag to make it bold */}
      <b>{msg}</b> {t("_FRG_IS_A_DIR")}
    </span>
  );

  // Determine which icon to use for each node in the tree
  const Icon = (node) => {
    return <span className={iconByType[node?.type] || ''}></span>
  };
  const titleRenderHandler = (node) => {
    return (
      <span data-key={`${node.key}`} title={node.title}>
        {node.title}
      </span>
    );
  };
  // Determine which switcher icon to use for each node in the tree
  const switcherIcon = (obj) => {
    if (obj.isLeaf) return;
    if (obj.expanded) return <ArrowDropDownIcon />;
    else return <ArrowRightIcon sx={{ color: "#666666" }} />;
  };

  // Handle changes in the selected account
  const handleAccountChange = (val) => {
    setAccountValue(val);
    if (val) {
      setCopyTempTreeData([]);
      dispatch(newTemplateActions.setTemplateName(""));
      dispatch(newTemplateActions.setCopyTemplateTreeSelectedNode(""));
      // getTreeData(val);
    }
  };

  return (
    <div>
      <Stack spacing={"8px"}>
        {/* <div className="dialog-header">
          <div className="title">
            {t("DIAG_INBOUND_WIZARD3_DESC")}
          </div>
        </div> */}
        <FormControl variant="outlined" className="width-100" size="small">
          <InputLabel id="accountDropdownLabel">{t("ACCOUNT_")}</InputLabel>
          <Select
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 300,
                },
              },
            }}
            className={"width-100"}
            name={"ACCOUNT"}
            value={accountValue}
            labelId="accountDropdownLabel"
            label={t("ACCOUNT_")}
            id="accountDropdown"
            disabled={accountsLoading || loading }
            onChange={(e) => {
              handleAccountChange(e.target.value);
            }}>
            {accountDropDownItems}
          </Select>
        </FormControl>
        <p ref={accountErrorRef} className="error"></p>
        {
          loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px', maxHeight:'300px' }}>
              <CircularProgress />
            </div>
          ) : (
            copyTempTreeData.length ? (
                <div className="ntw-scrollable-container" ref={parentRef}>
                  <Tree
                    className="myCls"
                    selectedKeys={selectedKeys}
                    expandedKeys={expandedKeys}
                    showLine
                    onExpand={onExpand}
                    onSelect={onSelect}
                    treeData={copyTempTreeData}
                    icon={Icon}
                    showIcon
                    switcherIcon={switcherIcon}
                    titleRender={titleRenderHandler}
                  />
                </div>
            ) : (
              <div className="panel-no-data">
                {t('NO_WFG_FOUND_FOR_ACC')}
              </div>
            )
          )
        }
        <p className="error" ref={errorRef}></p>
      </Stack>
      
      {errorModal && (
        <ErrorModal
          errorModal={errorModal}
          errorMessage={errorMessage}
          handleErrorModal={handleErrorModal}></ErrorModal>
      )}
    </div>
  );
}

export default NtwCopyTemplate